import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
} from 'firebase/firestore'
import { db, COLLECTIONS } from '@/lib/firebase'
import { getQueryDocsCacheFirst, subscribeQuery } from '@/lib/firestoreCache'
import type { Category } from '@/types'

const col = collection(db, COLLECTIONS.categories)

function mapCategory(id: string, data: Record<string, unknown>): Category {
  return {
    id,
    name: (data.name as string) || '',
    description: (data.description as string) || undefined,
    order: Number(data.order) || 0,
    active: data.active !== false,
    createdAt: (data.createdAt as string) || new Date().toISOString(),
    updatedAt: (data.updatedAt as string) || new Date().toISOString(),
  }
}

const categoriesQuery = query(col, orderBy('order', 'asc'))

function mapCategories(
  docs: { id: string; data: () => Record<string, unknown> }[]
): Category[] {
  return docs.map((d) => mapCategory(d.id, d.data()))
}

export async function fetchCategories(): Promise<Category[]> {
  const docs = await getQueryDocsCacheFirst(categoriesQuery)
  return mapCategories(docs)
}

/** Cache-first live updates (offline persistence). */
export function subscribeCategories(
  onData: (categories: Category[]) => void,
  onError?: (error: Error) => void
) {
  return subscribeQuery(categoriesQuery, mapCategories, onData, onError)
}

export async function fetchActiveCategories(): Promise<Category[]> {
  const all = await fetchCategories()
  return all.filter((c) => c.active)
}

export async function createCategory(
  data: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const now = new Date().toISOString()
  const ref = await addDoc(col, { ...data, createdAt: now, updatedAt: now })
  return ref.id
}

export async function updateCategory(
  id: string,
  data: Partial<Category>
): Promise<void> {
  const { id: _, createdAt, ...rest } = data
  await updateDoc(doc(db, COLLECTIONS.categories, id), {
    ...rest,
    updatedAt: new Date().toISOString(),
  })
}

export async function deleteCategory(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.categories, id))
}
